import React, { useEffect, useMemo, useState } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation } from "react-router-dom";
import { DollarOutlined } from "@ant-design/icons";
import { MdOutlineDashboard } from "react-icons/md";
import { FiUsers } from "react-icons/fi";
import { TbTruckDelivery } from "react-icons/tb";
import { RiFile2Line } from "react-icons/ri";
import { MdOutlineSettings } from "react-icons/md";
import { RxDotFilled } from "react-icons/rx";
import { ROLES } from "../constants";
import { MdOutlineAccountBalance } from "react-icons/md";
import { RiUserSettingsLine } from "react-icons/ri";
import { RiCalendarScheduleLine } from "react-icons/ri";
import { IoChatbubblesOutline, IoStopwatchOutline, IoVideocamOutline } from "react-icons/io5";
import { LiaTruckLoadingSolid } from "react-icons/lia";
import { FaUserCog, FaWpforms } from "react-icons/fa";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { GiTowTruck } from "react-icons/gi";
import { LiaFileInvoiceDollarSolid } from "react-icons/lia";
import { BsFileEarmark } from "react-icons/bs";
import { useSelector } from "react-redux";
import { IoHelpBuoyOutline } from "react-icons/io5";
import { RiFeedbackLine } from "react-icons/ri";
import { HiSpeakerphone } from "react-icons/hi";
import { PiFlagBannerFoldBold } from "react-icons/pi";
import BottomSidebar from "./BottomSidebar";

const { Sider } = Layout;

const ICON_SIZE = 16; // Adjust size as needed

const Sidebar = () => {
  const role = localStorage.getItem("role");
  const location = useLocation();

  // permissions array in Redux: e.g. ['VIEW_APPOINTMENTS', ...]
  const permissions = useSelector((state) => state.permissions.data || []);
  const loginKey = useSelector((state) => state.permissions.loginKey); // triggers rebuild on login

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  // runtime permission checker
  const hasPerm = (permName) => {
    // guard: if permissions is not array, treat as empty
    if (!Array.isArray(permissions)) return false;
    return permissions.includes(permName);
  };

  // Build sidebar items reactively (recompute when permissions, role, or loginKey changes)
  const sidebarItems = useMemo(() => {
    // helper builders for children arrays
    const helpChild = (role === ROLES.EMPLOYEE) ? [
      {
        label: "Knowledge Base",
        key: "/help-center/knowledge-base",
        link: "/help-center/knowledge-base",
        icon: <RiFile2Line />,
      },
    ] : [
      {
        label: "Knowledge Base",
        key: "/help-center/knowledge-base",
        link: "/help-center/knowledge-base",
        icon: <RiFile2Line />,
      },
      {
        label: "Support",
        key: "/help-center/support",
        link: "/help-center/support",
        icon: <LiaFileInvoiceDollarSolid />,
      },
      {
        label: "Feedback",
        key: "/help-center/feedback",
        link: "/help-center/feedback",
        icon: <RiFeedbackLine />,
      },
    ];


    const dashboardChild = [
      { label: "Management Dashboard", key: "Management", link: "/management-dashboard", icon: <MdOutlineDashboard /> },
      { label: "Operations Dashboard", key: "Operations", link: "/operation-dashboard", icon: <MdOutlineDashboard /> },
      { label: "Accounts Dashboard", key: "Accounts", link: "/account-dashboard", icon: <MdOutlineDashboard /> },
    ].filter(Boolean);

    const operationChild = [
      hasPerm("VIEW_VEHICLES") && { label: "Vehicles", key: "/vehicle", link: "/vehicle", icon: <TbTruckDelivery /> },
      hasPerm("VIEW_CUSTOMERS") && { label: "Customers", key: "/operations/customer", link: "/operations/customer", icon: <MdOutlineDashboard /> },
      hasPerm("VIEW_DRIVERS") && { label: "Drivers", key: "/operations/driver", link: "/operations/driver", icon: <MdOutlineDashboard /> },
      hasPerm("VIEW_VENDORS") && { label: "Vendors", key: "/operations/vendor", link: "/operations/vendor", icon: <MdOutlineDashboard /> },
      hasPerm("VIEW_LOADPLANS") && { label: "Loading Plan", key: "/operations/loadings", link: "/operations/loadings", icon: <LiaTruckLoadingSolid /> },
      hasPerm("VIEW_TOWINGTARIFF") && { label: "Towing Tariff", key: "/operations/tariffs", link: "/operations/tariffs", icon: <GiTowTruck /> },
      hasPerm("VIEW_BLFORM") && { label: "BL Form/Doc Receipt", key: "/operations/bl-forms", link: "/operations/bl-forms", icon: <FaWpforms /> },
      hasPerm("VIEW_QUOTATIONS") && { label: "Quotations", key: "/operations/quotation", link: "/operations/quotation", icon: <RiFile2Line /> },
      // Rate Sending seems always present in your code: keep it if desired
      { label: "Rate Sending", key: "/operations/rate-sending", link: "/operations/rate-sending", icon: <DollarOutlined /> },
      hasPerm("VIEW_APPOINTMENTS") && { label: "Appointments", key: "/operations/appointment", link: "/operations/appointment", icon: <IoStopwatchOutline /> },
      hasPerm("VIEW_INVENTORY_REPORTS") && { label: "Inventory Report", key: "/operations/inventoryreport", link: "/operations/inventoryreport", icon: <HiOutlineDocumentReport /> },
      hasPerm("VIEW_BOOKING") && { label: "Schedule Booking", key: "/operations/bookings-schedules", link: "/operations/bookings-schedules", icon: <RiCalendarScheduleLine /> },
    ].filter(Boolean);

    const accountsChild = [
      { label: "PNL", key: "/accounts/PNL", link: "/accounts/PNL", icon: <RiFile2Line /> },
      hasPerm('VIEW_LEDGER') && { label: "Ledger", key: "/accounts/ledger", link: "/accounts/ledger", icon: <BsFileEarmark /> },
      { label: "Invoices", key: "/invoice", link: "/invoice", icon: <LiaFileInvoiceDollarSolid /> },
      { label: "Reports", key: "/accounts/reports", link: "/accounts/reports", icon: <HiOutlineDocumentReport /> },
    ].filter(Boolean);

    const administrateChild = [
      hasPerm('VIEW_EMPLOYEES') && { label: "Employees", key: "/staff/user", link: "/staff/user", icon: <MdOutlineDashboard /> },
      { label: "Roles", key: "/staff/role", link: "/staff/role", icon: <FaUserCog /> },
      hasPerm('VIEW_SETTINGS') && { label: "Settings", key: "/administrations/settings", link: "/administrations/settings", icon: <MdOutlineSettings /> },
      {
        label: "Feedback",
        key: "/feedback",
        link: "/feedback",
        icon: <RiFeedbackLine />,
      },
      {
        label: "Video Tutorial",
        key: "/video",
        link: "/video",
        icon: <IoVideocamOutline />,
      },
      {
        label: "Tickers",
        key: "/tickers",
        link: "/tickers",
        icon: <HiSpeakerphone />,
      },
      {
        label: "Banner",
        key: "/banner",
        link: "/banner",
        icon: <PiFlagBannerFoldBold />,
      },
    ].filter(Boolean);

    const additionalFeatures = [
      hasPerm('BROWSE_TRANSACTION') && { label: "Browse Transaction", key: "/accounts/browse-transaction", link: "/accounts/browse-transaction", icon: <RxDotFilled /> },
      { label: "Vendor Payments List", key: "/accounts/vendor-payment-lists", link: "/accounts/vendor-payment-lists", icon: <RxDotFilled /> },
    ].filter(Boolean);

    const additionalFeatures2 = [
      { label: "Expense Voucher", key: "/accounts/expense-vouchers", link: "/accounts/expense-vouchers", icon: <RxDotFilled /> },
      { label: "Payment Voucher", key: "/accounts/payment-vouchers", link: "/accounts/payment-vouchers", icon: <RxDotFilled /> },
      { label: "Credit Note", key: "/accounts/credit-notes", link: "/accounts/credit-notes", icon: <RxDotFilled /> },
      { label: "Debit Note", key: "/accounts/debit-notes", link: "/accounts/debit-notes", icon: <RxDotFilled /> },
      { label: "Invoice Receipt", key: "/accounts/receipt-collects", link: "/accounts/receipt-collects", icon: <RxDotFilled /> },
      { label: "Costing Expense", key: "/accounts/costings", link: "/accounts/costings", icon: <RxDotFilled /> },
      { label: "Customer Balance", key: "/accounts/customer-ledgers", link: "/accounts/customer-ledgers", icon: <RxDotFilled /> },
      { label: "Vendors Containers Payments", key: "/accounts/vendor-container-payments", link: "/accounts/vendor-container-payments", icon: <RxDotFilled /> },
    ];

    // top-level admin sidebar (only visible to admin/employee per your logic)
    const adminSidebar = [
      hasPerm("VIEW_DASHBOARD") && {
        label: <Link to="/">Dashboard</Link>,
        key: "/",
        // link: "/",
        icon: <MdOutlineDashboard size={ICON_SIZE} />,
        children: dashboardChild.map(i => ({ ...i, icon: React.cloneElement(i.icon, { size: ICON_SIZE }) })),

      },
      {
        label: "Operations",
        key: "operations",
        icon: <FiUsers size={ICON_SIZE} />,
        children: operationChild.map(i => ({ ...i, icon: React.cloneElement(i.icon, { size: ICON_SIZE }) })),
      },
      hasPerm("VIEW_ACCOUNTS") && {
        label: <Link to="/accounts">Accounts</Link>,
        key: "accounts",
        link: "/accounts",
        icon: <MdOutlineAccountBalance size={ICON_SIZE} />,
        children: [
          ...accountsChild.map(item => ({ ...item, icon: React.cloneElement(item.icon, { size: ICON_SIZE }) })),
          { type: "divider" },
          ...additionalFeatures.map(item => ({ ...item, icon: React.cloneElement(item.icon, { size: ICON_SIZE }) })),
          { type: "divider" },
          ...additionalFeatures2.map(item => ({ ...item, icon: React.cloneElement(item.icon, { size: ICON_SIZE }) })),
        ],
      },
      // hasPerm("VIEW_ADMINISTRATION") && 
      (role === ROLES.ADMIN) &&
      {
        label: "Administrations",
        key: "Administrations",
        link: "",
        icon: <RiUserSettingsLine size={ICON_SIZE} />,
        children: administrateChild.map(item => ({ ...item, icon: React.cloneElement(item.icon, { size: ICON_SIZE }) })),
      },

      ...(role === ROLES.EMPLOYEE
        ? [
          {
            label: <Link to="/chat">Chat Console</Link>,
            key: "/chat",
            link: "/chat",
            icon: <IoChatbubblesOutline size={ICON_SIZE} />,
          },
          {
            label: 'Help Center',
            key: "help-center",
            link: "",
            icon: <IoHelpBuoyOutline size={ICON_SIZE} />,
            children: helpChild,
          }
        ]
        : []
      )

    ].filter(Boolean);

    const customerItems = [
      { label: <Link to="/">Dashboard</Link>, key: "/", link: "/", icon: <MdOutlineDashboard size={ICON_SIZE} /> },
      { label: <Link to="/vehicle">Vehicles</Link>, key: "/vehicle", link: "/vehicle", icon: <TbTruckDelivery size={ICON_SIZE} /> },
      { label: <Link to="/invoice">Invoices</Link>, key: "/invoice", link: "/invoice", icon: <LiaFileInvoiceDollarSolid size={ICON_SIZE} /> },
      { label: <Link to="/ledger">Ledger</Link>, key: "/ledger", link: "/ledger", icon: <MdOutlineDashboard size={ICON_SIZE} /> },
      { label: <Link to="/customers-portal/load-plan/loaded-plan">Loading Plan</Link>, key: "/customers-portal/load-plan", link: "/customers-portal/load-plan", icon: <LiaTruckLoadingSolid size={ICON_SIZE} /> },
      { label: <Link to="/customers-portal/appointments">Appointment</Link>, key: "/customers-portal/appointments", link: "/customers-portal/appointments", icon: <IoStopwatchOutline size={ICON_SIZE} /> },
      // { label: " Help Center", key: "help-center", icon: <IoHelpBuoyOutline size={ICON_SIZE} />, children: helpChild }
    ];

    const driverSidebar = [
      { label: <Link to="/operations/appointment">Appointment</Link>, key: "/operations/appointment", link: "/operations/appointment", icon: <IoStopwatchOutline size={ICON_SIZE} /> },
    ];

    // pick which set based on role
    if (role === ROLES.ADMIN || role === ROLES.EMPLOYEE) return adminSidebar;
    if (role === ROLES.CUSTOMER) return customerItems;
    if (role === ROLES.DRIVER) return driverSidebar;
    return [];
  }, [permissions, role, loginKey]); // recompute when permissions or loginKey change

  // useEffect(() => {
  //   const roleLS = localStorage.getItem("role");
  //   console.log("DEBUG: role(localStorage) =", roleLS);
  //   console.log("DEBUG: ROLES constant =", ROLES);
  //   console.log("DEBUG: redux permissions =", permissions);
  //   console.log("DEBUG: contains VIEW_ADMINISTRATION?", Array.isArray(permissions) && permissions.includes("VIEW_ADMINISTRATION"));
  // }, [permissions]);

  // Keep items in state so Menu re-renders
  const [items, setItems] = useState(sidebarItems);
  useEffect(() => {
    setItems(sidebarItems);
  }, [sidebarItems]);

  // compute selected / open keys on path change and items change
  useEffect(() => {
    const currentPath = location.pathname;

    const flattenItems = (menuItems, parent = null) => {
      return menuItems.flatMap((item) => {
        if (item.children) {
          return [{ ...item, parent }].concat(flattenItems(item.children, item));
        }
        return [{ ...item, parent }];
      });
    };

    const flatItems = flattenItems(items);

    let matchedItem = null;
    for (const item of flatItems) {
      if (item.link && currentPath.startsWith(item.link)) {
        if (!matchedItem || item.link.length > matchedItem.link.length) {
          matchedItem = item;
        }
      }
    }

    if (matchedItem) {
      if (matchedItem.parent) {
        setOpenKeys([matchedItem.parent.key]);
      } else {
        setOpenKeys([]);
      }
      setSelectedKeys([matchedItem.link || matchedItem.key]);
    } else {
      setOpenKeys([]);
      setSelectedKeys([]);
    }
  }, [location.pathname, items]);

  const handleOpenChange = (keys) => {
    const latestKey = keys[keys.length - 1];
    setOpenKeys(latestKey ? [latestKey] : []);
  };

  return (
    <div>

      <Sider
        width={270}
        style={{
          display: "inline-block", // 🔥 CHANGE THIS
          height: "auto",
          background: "#FFFFFF",
          border: "1px solid #E8E8E9",
          borderRadius: 20,
          marginTop: 40,
          // display: "flex",
          // flexDirection: "column",
          // height: "fit-content",
          // overflow: "hidden",
          // overflow: "auto",
          // height: "50%",
          // top: 50,
          padding: 0,
          // position: "sticky",
        }}
        className="sidebar-scrollable"
      >
        <Menu
          mode="inline"
          className="sidebar-menu"
          openKeys={openKeys}
          selectedKeys={selectedKeys}
          onOpenChange={handleOpenChange}
          style={{
            padding: 0,
            borderRight: "none",
            height: "auto"
          }}
        >
          {items.map((item) =>
            item.children ? (
              <Menu.SubMenu key={item.key} icon={item.icon} title={item.label} style={{ borderBottom: "1px solid #E8E8E9" }}>
                {item.children.map((subItem, subIndex) =>
                  subItem.type === "divider" ? (
                    <Menu.Divider key={`divider-${subIndex}`} style={{ margin: 7, borderBottom: "1px solid #E8E8E9" }} />
                  ) : (
                    <Menu.Item key={subItem.link || subItem.key} icon={subItem.icon}>
                      <Link to={subItem.link || subItem.key}>{subItem.label}</Link>
                    </Menu.Item>
                  )
                )}
              </Menu.SubMenu>
            ) : (
              <Menu.Item key={item.link || item.key} icon={item.icon} style={{ borderBottom: "1px solid #E8E8E9", borderRadius: 0 }}>
                <Link to={item.link || item.key}>{item.label}</Link>
              </Menu.Item>
            )
          )}
        </Menu>
      </Sider>

      {role === ROLES.CUSTOMER && <BottomSidebar role={role} />}
    </div>


  );
};

export default Sidebar;
